// frontend/src/components/fleet/FleetOperationalView.jsx
//
// Contenedor de la vista operativa: lista de vehículos a la izquierda y mapa GPS
// (con geocercas) a la derecha, con la selección sincronizada en ambos sentidos.
//
// Modos:
//   'split' → lista + mapa (desktop)
//   'list'  → solo lista
//   'map'   → solo mapa
//
// Los datos vienen de `/fleet/overview` y `/fleet/map`; el backend es la fuente
// de verdad de estados, velocidades, distancias y estado por llanta.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { api } from '../../services/api';
import FleetListView from './FleetListView';
import FleetMapPanel from './FleetMapPanel';

const FleetOperationalView = ({
  viewMode = 'split',
  searchTerm = '',
  windowHours = 24,
  refreshKey = 0,
  vehicleIds = null,
}) => {
  const { t } = useTranslation();
  const [overview, setOverview] = useState([]);
  const [mapData, setMapData] = useState({ markers: [], geofences: [] });
  const [loading, setLoading] = useState(true);
  const [mapLoading, setMapLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = { window_hours: windowHours };
    if (vehicleIds) params.vehicle_ids = vehicleIds;
    api.fleet.getOverview(params)
      .then((res) => { if (!cancelled) setOverview(res.data?.vehicles || []); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail || 'No se pudo cargar la vista de flota');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [windowHours, refreshKey, vehicleIds]);

  useEffect(() => {
    if (viewMode === 'list') return undefined;
    let cancelled = false;
    setMapLoading(true);
    api.fleet.getMap()
      .then((res) => {
        if (!cancelled) {
          setMapData({
            markers: res.data?.markers || [],
            geofences: res.data?.geofences || [],
          });
        }
      })
      .catch(() => { if (!cancelled) setMapData({ markers: [], geofences: [] }); })
      .finally(() => { if (!cancelled) setMapLoading(false); });
    return () => { cancelled = true; };
  }, [viewMode, refreshKey]);

  const filtered = useMemo(() => {
    const term = String(searchTerm || '').trim().toLowerCase();
    if (!term) return overview;
    return overview.filter((v) => [v.vehicle_name, v.alias, v.type, v.make, v.model]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(term)));
  }, [overview, searchTerm]);

  // El mapa solo muestra las unidades visibles en la lista y hereda su estado.
  const markers = useMemo(() => {
    const statusById = new Map(filtered.map((v) => [v.vehicle_id, v.status]));
    return (mapData.markers || [])
      .filter((m) => statusById.has(m.vehicle_id))
      .map((m) => ({ ...m, status: statusById.get(m.vehicle_id) }));
  }, [mapData.markers, filtered]);

  const handleSelect = useCallback((vehicleId) => {
    setSelectedVehicleId((current) => (current === vehicleId ? current : vehicleId));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  const withoutLocation = filtered.length - markers.length;

  const listPanel = (
    <FleetListView
      vehicles={filtered}
      selectedVehicleId={selectedVehicleId}
      onSelectVehicle={handleSelect}
      showDetailMap={viewMode !== 'split'}
    />
  );

  const mapPanel = (
    <Box sx={{ position: 'sticky', top: 12 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant="overline" color="text.secondary">
          {t('hardcoded.components_fleet.ubicacion')}
        </Typography>
        <Chip size="small" label={`${markers.length} con GPS`} />
        {withoutLocation > 0 && (
          <Chip size="small" variant="outlined" label={`${withoutLocation} sin GPS`} />
        )}
        {(mapData.geofences || []).length > 0 && (
          <Chip size="small" variant="outlined" color="primary"
            label={`${mapData.geofences.length} ${t('hardcoded.components_fleet.geocercas').toLowerCase()}`} />
        )}
      </Stack>
      <FleetMapPanel
        markers={markers}
        geofences={mapData.geofences}
        selectedVehicleId={selectedVehicleId}
        onSelectVehicle={handleSelect}
        loading={mapLoading}
        height={viewMode === 'map' ? 640 : 560}
      />
    </Box>
  );

  if (viewMode === 'list') return listPanel;
  if (viewMode === 'map') return mapPanel;

  // En pantallas chicas el mapa va ARRIBA: si quedara debajo de la lista habría
  // que recorrer toda la flota para llegar a él.
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={5} sx={{ order: { xs: 0, lg: 1 } }}>{mapPanel}</Grid>
      <Grid item xs={12} lg={7} sx={{ order: { xs: 1, lg: 0 } }}>{listPanel}</Grid>
    </Grid>
  );
};

FleetOperationalView.propTypes = {
  viewMode: PropTypes.oneOf(['split', 'list', 'map']),
  searchTerm: PropTypes.string,
  windowHours: PropTypes.number,
  refreshKey: PropTypes.number,
  vehicleIds: PropTypes.string,
};

export default FleetOperationalView;
